import { ChevronDown, ChevronRight, Download, Edit, Plus, Trash2 } from 'lucide-react';
import Button from '../../../components/Button';
import EmptyState from '../../../components/EmptyState';
import StatusBadge from './StatusBadge';
import LessonCard from './LessonCard';
import type { LearningLesson, LearningSection } from '../services/learningTypes';

interface SectionCardProps {
    section: LearningSection;
    isExpanded: boolean;
    copiedLessonId: string | null;
    onToggle: () => void;
    onEdit: () => void;
    onDelete: () => void;
    onExportSection: () => void;
    onExportLessons: () => void;
    onPasteLesson: () => void;
    onPasteTextFormat: () => void;
    onAddLesson: () => void;
    onEditLesson: (lesson: LearningLesson) => void;
    onDeleteLesson: (lesson: LearningLesson) => void;
    onCopyLesson: (lesson: LearningLesson) => void;
    onPreviewLesson: (lesson: LearningLesson) => void;
}

const SectionCard = ({
    section,
    isExpanded,
    copiedLessonId,
    onToggle,
    onEdit,
    onDelete,
    onExportSection,
    onExportLessons,
    onPasteLesson,
    onPasteTextFormat,
    onAddLesson,
    onEditLesson,
    onDeleteLesson,
    onCopyLesson,
    onPreviewLesson,
}: SectionCardProps) => (
    <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-4 min-w-0">
                    <Button
                        onClick={onToggle}
                        variant="ghost"
                        icon={isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                    />
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 break-words">{section.title}</h3>
                        <p className="text-gray-600 text-sm break-words">{section.subtitle}</p>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                            <StatusBadge status={section.status} />
                            <span className="text-xs text-gray-500">Order: {section.order}</span>
                            <span className="text-xs text-gray-500">
                                {section.lessons.length} lesson{section.lessons.length !== 1 ? 's' : ''}
                            </span>
                            {section.isSupportOnly && (
                                <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded font-medium">
                                    Support Only
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-3 items-start md:items-end">
                    <div className="flex items-center gap-2">
                        <Button onClick={onEdit} variant="primary" size="sm" icon={<Edit size={16} />} />
                        <Button onClick={onDelete} variant="danger" size="sm" icon={<Trash2 size={16} />} />
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:justify-end">
                        <Button
                            onClick={onExportSection}
                            variant="outline"
                            size="sm"
                            icon={<Download size={16} />}
                            title="Export entire section"
                        >
                            Export Section
                        </Button>
                        {section.lessons.length > 0 && (
                            <Button
                                onClick={onExportLessons}
                                variant="outline"
                                size="sm"
                                icon={<Download size={16} />}
                                title="Export selected lessons"
                            >
                                Export Lessons
                            </Button>
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:justify-end">
                        <Button onClick={onPasteLesson} variant="primary" size="sm" icon={<Plus size={16} />}>
                            Paste Lesson
                        </Button>
                        <Button onClick={onPasteTextFormat} variant="primary" size="sm" icon={<Plus size={16} />}>
                            Paste Text Format
                        </Button>
                        <Button onClick={onAddLesson} variant="success" size="sm" icon={<Plus size={16} />}>
                            Add Lesson
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        {isExpanded && (
            <div className="p-4 sm:p-6">
                {section.lessons.length === 0 ? (
                    <EmptyState
                        title="No lessons in this section yet"
                        description="Add your first lesson to get started with this section."
                        action={{
                            label: "Add First Lesson",
                            onClick: onAddLesson,
                            variant: "secondary",
                        }}
                    />
                ) : (
                    <div className="grid gap-4">
                        {section.lessons
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((lesson) => (
                                <LessonCard
                                    key={lesson.uid}
                                    lesson={lesson}
                                    isCopied={copiedLessonId === lesson.uid}
                                    onCopy={() => onCopyLesson(lesson)}
                                    onEdit={() => onEditLesson(lesson)}
                                    onDelete={() => onDeleteLesson(lesson)}
                                    onPreview={() => onPreviewLesson(lesson)}
                                />
                            ))}
                    </div>
                )}
            </div>
        )}
    </div>
);

export default SectionCard;
